import React, { useEffect, useRef, useState } from 'react';
import './About.css';
import { FaMapMarkerAlt, FaBriefcase, FaCertificate, FaShieldAlt, FaUserTie, FaAward, FaStar } from 'react-icons/fa';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ experience: 0, clients: 0, satisfaction: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            startCounters();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const startCounters = () => {
    // Experience counter
    let exp = 0;
    const expInterval = setInterval(() => {
      exp += 1;
      setCounters(prev => ({ ...prev, experience: exp }));
      if (exp >= 20) clearInterval(expInterval);
    }, 50);

    // Clients counter
    let clients = 0;
    const clientsInterval = setInterval(() => {
      clients += 10;
      setCounters(prev => ({ ...prev, clients: clients }));
      if (clients >= 500) clearInterval(clientsInterval);
    }, 20);

    // Satisfaction counter
    let satisfaction = 0;
    const satisfactionInterval = setInterval(() => {
      satisfaction += 2;
      setCounters(prev => ({ ...prev, satisfaction: satisfaction }));
      if (satisfaction >= 100) clearInterval(satisfactionInterval);
    }, 20);
  };

  return (
    <section id="a-propos" className="section about" ref={sectionRef}>
      <div className="container">
        <div className="section-title">
          <h2>À Propos</h2>
          <p>Votre plombier de confiance depuis plus de 20 ans</p>
        </div>

        <div className="about-content">
          {/* Left Content - Text */}
          <div className={`about-text ${isVisible ? 'reveal active' : 'reveal'}`}>
            <div className="about-intro">
              <div className="intro-icon">
                <FaUserTie />
              </div>
              <h3>PlumbThe2nd - Expert en Plomberie</h3>
            </div>

            <div className="about-story">
              <p className="about-highlight">
                Fort de <strong>plus de 20 années d'expérience</strong> dans le domaine de la plomberie, 
                je mets mon expertise et mon savoir-faire au service des particuliers et des professionnels.
              </p>
              <p>
                Mon parcours professionnel diversifié s'étend sur différentes structures : 
                <strong> 12 ans en tant que salarié</strong>, <strong>2 ans comme auto-entrepreneur</strong> 
                et <strong>3 ans en SASU</strong>. Cette riche expérience me permet de comprendre 
                parfaitement vos besoins et d'y répondre avec efficacité et professionnalisme.
              </p>
              <p>
                Ma priorité absolue reste votre <strong>satisfaction</strong> et la <strong>qualité</strong> 
                de mes prestations. Chaque intervention est réalisée dans le respect des normes en vigueur 
                et avec le souci du détail qui fait la différence.
              </p>
            </div>

            {/* Certifications */}
            <div className="about-certifications">
              <h4>
                <FaAward /> Garanties & Certifications
              </h4>
              <div className="cert-grid">
                <div className="cert-card">
                  <div className="cert-icon">
                    <FaShieldAlt />
                  </div>
                  <div className="cert-info">
                    <strong>Garantie Décennale</strong>
                    <p>Protection de vos travaux pendant 10 ans</p>
                  </div>
                </div>
                <div className="cert-card">
                  <div className="cert-icon">
                    <FaCertificate />
                  </div>
                  <div className="cert-info">
                    <strong>RC Professionnelle</strong>
                    <p>Assurance complète pour votre tranquillité</p>
                  </div>
                </div>
                <div className="cert-card">
                  <div className="cert-icon">
                    <FaBriefcase />
                  </div>
                  <div className="cert-info">
                    <strong>Entreprise Légale</strong>
                    <p>SIRET vérifié et enregistré</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Stats */}
          <div className={`about-stats-wrapper ${isVisible ? 'reveal active' : 'reveal'}`}>
            {/* Animated Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card stat-card-1">
                <div className="stat-icon-wrapper">
                  <FaBriefcase className="stat-icon" />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{counters.experience}+</div>
                  <div className="stat-label">Ans d'Expérience</div>
                </div>
                <div className="stat-progress">
                  <div className="stat-progress-bar" style={{ width: isVisible ? '100%' : '0%' }}></div>
                </div>
              </div>

              <div className="stat-card stat-card-2">
                <div className="stat-icon-wrapper">
                  <FaStar className="stat-icon" />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{counters.clients}+</div>
                  <div className="stat-label">Clients Satisfaits</div>
                </div>
                <div className="stat-progress">
                  <div className="stat-progress-bar" style={{ width: isVisible ? '100%' : '0%' }}></div>
                </div>
              </div>

              <div className="stat-card stat-card-3">
                <div className="stat-icon-wrapper">
                  <FaShieldAlt className="stat-icon" />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{counters.satisfaction}%</div>
                  <div className="stat-label">Travaux Garantis</div>
                </div>
                <div className="stat-progress">
                  <div className="stat-progress-bar" style={{ width: isVisible ? '100%' : '0%' }}></div>
                </div>
              </div>

              <div className="stat-card stat-card-4">
                <div className="stat-icon-wrapper">
                  <FaMapMarkerAlt className="stat-icon" />
                </div>
                <div className="stat-content">
                  <div className="stat-number">7j/7</div>
                  <div className="stat-label">Disponibilité</div>
                </div>
                <div className="stat-progress">
                  <div className="stat-progress-bar" style={{ width: isVisible ? '100%' : '0%' }}></div>
                </div>
              </div>
            </div>

            {/* Experience Timeline */}
            <div className="experience-timeline">
              <h4>Parcours Professionnel</h4>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-year">12 ans</div>
                    <div className="timeline-text">Salarié - Formation et expertise</div>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-year">2 ans</div>
                    <div className="timeline-text">Auto-entrepreneur - Autonomie</div>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-year">3 ans</div>
                    <div className="timeline-text">SASU - Structure professionnelle</div>
                  </div>
                </div>
                <div className="timeline-item active">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-year">Aujourd'hui</div>
                    <div className="timeline-text">Excellence et innovation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className={`trust-badges ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="trust-badge">
            <FaShieldAlt />
            <span>Assuré & Certifié</span>
          </div>
          <div className="trust-badge">
            <FaStar />
            <span>5 étoiles sur Google</span>
          </div>
          <div className="trust-badge">
            <FaCertificate />
            <span>Garantie Décennale</span>
          </div>
          <div className="trust-badge">
            <FaAward />
            <span>Artisan Qualifié</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
